import { base64url, canonical, unbase64url, verifyChallenge } from "../_shared/syncCryptography.ts";
import { type ControlState, consumeProof, type Device, type Purpose, type Root } from "./model.ts";

export type SignedRequest = {
  action: string;
  requestId: string;
  issuedAt: number;
  deviceId: string;
  signature: string;
  payload: Record<string, unknown>;
};
export async function digest(value: unknown) {
  return base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical(value)))));
}
export function object(value: unknown): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("sync-request-format");
}
export function exact(value: Record<string, unknown>, required: string[], optional: string[] = []) {
  if (
    required.some((k) => !(k in value)) ||
    Object.keys(value).some((k) => !required.includes(k) && !optional.includes(k))
  )
    throw new Error("sync-request-format");
}
export function id(value: unknown): string {
  if (typeof value !== "string" || !/^[\w-]{1,128}$/.test(value)) throw new Error("sync-request-format");
  return value;
}
export function publicJwk(value: unknown): JsonWebKey {
  object(value);
  exact(value, ["kty", "crv", "x", "y"], ["ext", "key_ops", "alg"]);
  if (
    value.kty !== "EC" ||
    value.crv !== "P-256" ||
    typeof value.x !== "string" ||
    typeof value.y !== "string" ||
    unbase64url(value.x, 32).length !== 32 ||
    unbase64url(value.y, 32).length !== 32
  )
    throw new Error("sync-public-key-format");
  return { kty: "EC", crv: "P-256", x: value.x, y: value.y, ext: true };
}
export function activeDevice(state: ControlState, deviceId: string): Device {
  const device = state.devices.find((d) => d.id === deviceId && !d.revokedAt);
  if (!device) throw new Error("sync-device-revoked");
  return device;
}
export function hasRecoveryFile(root: Root, devices: Device[]) {
  // Device setup also sets recoveryVerified to prove signer possession. Older
  // recovery-file updates kept recoveryMode, but replaced the device signing key.
  return (
    root.recoveryMode !== "device" ||
    !devices.some((device) => canonical(publicJwk(device.signing)) === canonical(publicJwk(root.recoveryPublicKey)))
  );
}
export async function authenticateDevice(
  accountId: string,
  state: ControlState,
  input: unknown,
  now: number,
  candidate?: JsonWebKey,
): Promise<SignedRequest> {
  object(input);
  exact(input, ["action", "requestId", "issuedAt", "deviceId", "signature", "payload"]);
  const req = input as SignedRequest;
  object(req.payload);
  id(req.requestId);
  id(req.deviceId);
  id(req.action);
  if (
    !Number.isSafeInteger(req.issuedAt) ||
    Math.abs(req.issuedAt - now) > 60000 ||
    state.usedRequests.some((r) => r.id === req.requestId)
  )
    throw new Error("sync-request-replay");
  const key = candidate ?? activeDevice(state, req.deviceId).signing;
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicJwk(key),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["verify"],
  );
  const { signature, ...body } = req;
  if (!(await verifyChallenge(publicKey, { accountId, ...body }, signature))) throw new Error("sync-device-signature");
  if (state.usedRequests.length >= 2048) throw new Error("sync-rate-limit");
  state.usedRequests.push({ id: req.requestId, at: now });
  return req;
}
export async function stepUp(
  state: ControlState,
  request: SignedRequest,
  purpose: Purpose,
  target: unknown,
  now: number,
) {
  consumeProof(state, id(request.payload.proofId), request.deviceId, purpose, await digest(target), now);
}

// Envelopes must be opaque AES-GCM transport objects. This rejects accidental
// classroom JSON/private JWK upload to a service that must never see it.
export function opaqueCipher(input: unknown) {
  object(input);
  exact(input, ["header", "iv", "ciphertext"]);
  object(input.header);
  exact(input.header, ["datasetId", "generation", "keyVersion", "objectId", "format"]);
  id(input.header.datasetId);
  id(input.header.generation);
  id(input.header.objectId);
  if (
    input.header.format !== 2 ||
    !Number.isSafeInteger(input.header.keyVersion) ||
    Number(input.header.keyVersion) < 1 ||
    typeof input.iv !== "string" ||
    unbase64url(input.iv, 12).length !== 12 ||
    typeof input.ciphertext !== "string" ||
    unbase64url(input.ciphertext, 65536).length < 16
  )
    throw new Error("sync-envelope-format");
  return input;
}
export function deviceEnvelope(input: unknown) {
  object(input);
  exact(input, ["ephemeral", "cipher"]);
  publicJwk(input.ephemeral);
  opaqueCipher(input.cipher);
  return input;
}
export function envelopeScope(
  input: unknown,
  scope: { datasetId: string; generation: string; keyVersion: number; objectId: string },
) {
  const envelope = deviceEnvelope(input);
  const cipher = envelope.cipher as { header: Record<string, unknown> };
  if (canonical(cipher.header) !== canonical({ ...scope, format: 2 })) throw new Error("sync-envelope-scope");
}
